import React from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalOffer as TagsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../inputs/LanguageSelector';
import ClientTagsRow from '../clients/ClientTagsRow';
import { splitClientTagsBySource } from '../../utils/clientTags';
import { DEFAULT_CURRENCY_SYMBOL, DEFAULT_RESERVATION_LIMIT } from '../../constants/appConstants';
import { formatPricingRuleSummary, resolvePricingRules } from '../../utils/pricingRules';

function ProfileRow({ label, children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(120px, 160px) 1fr',
        gap: isMobile ? 0.25 : 2,
        alignItems: isMobile ? 'flex-start' : 'center',
        py: 0.75,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function ProfileGroup({ title, children, showDivider = true }) {
  return (
    <>
      {showDivider && <Divider sx={{ my: 1.5 }} />}
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4 }}
      >
        {title}
      </Typography>
      {children}
    </>
  );
}

function EmptyValue() {
  const { t } = useTranslation();
  return (
    <Typography variant="body2" color="text.disabled">
      {t('common.emptyValue')}
    </Typography>
  );
}

export default function ClientProfileTab({
  client,
  isManager,
  canManage,
  onEdit,
  onManageTags,
  onDelete,
  onCopyInvoiceId,
}) {
  const { t } = useTranslation();

  const getLanguageLabel = (code) => LANGUAGES.find((l) => l.code === code)?.label || code || '—';
  const { manual, rule } = splitClientTagsBySource(client?.clientTags || []);

  const reservationDisplay = client?.reservationLimit != null
    ? `${DEFAULT_CURRENCY_SYMBOL}${Number(client.reservationLimit).toLocaleString()}`
    : t('clients.profile.reservationDefault', {
      currency: DEFAULT_CURRENCY_SYMBOL,
      amount: DEFAULT_RESERVATION_LIMIT.toLocaleString(),
    });

  const addressLine = [client?.address, client?.city, client?.country].filter(Boolean).join(', ');
  const showTagsSection = manual.length > 0 || rule.length > 0 || canManage;

  return (
    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {(canManage || isManager) && (
        <>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}
          >
            {canManage && (
              <>
                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={onEdit}>
                  {t('clients.edit')}
                </Button>
                <Button size="small" variant="outlined" startIcon={<TagsIcon />} onClick={onManageTags}>
                  {t('clients.tags.manageTags')}
                </Button>
              </>
            )}
            {isManager && (
              <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
                {t('clients.delete')}
              </Button>
            )}
          </Stack>
          <Divider />
        </>
      )}

      <Box sx={{ px: 2, py: 1.5 }}>
        <ProfileGroup title={t('clients.profile.contactSection')} showDivider={false}>
          <ProfileRow label={t('clients.email')}>
            {client?.email ? (
              <Link href={`mailto:${client.email}`} variant="body2" underline="hover" sx={{ wordBreak: 'break-all' }}>
                {client.email}
              </Link>
            ) : (
              <EmptyValue />
            )}
          </ProfileRow>
          <ProfileRow label={t('clients.phone')}>
            {client?.phone ? (
              <Link href={`tel:${client.phone}`} variant="body2" underline="hover">
                {client.phone}
              </Link>
            ) : (
              <EmptyValue />
            )}
          </ProfileRow>
          <ProfileRow label={t('clients.language')}>
            <Typography variant="body2">{getLanguageLabel(client?.language)}</Typography>
          </ProfileRow>
        </ProfileGroup>

        <ProfileGroup title={t('clients.profile.businessSection')}>
          <ProfileRow label={t('clients.companyName')}>
            {client?.companyName ? (
              <Typography variant="body2">{client.companyName}</Typography>
            ) : (
              <EmptyValue />
            )}
          </ProfileRow>
          <ProfileRow label={t('clients.vatId')}>
            {client?.vatId ? (
              <Typography variant="body2">{client.vatId}</Typography>
            ) : (
              <EmptyValue />
            )}
          </ProfileRow>
        </ProfileGroup>

        <ProfileGroup title={t('clients.profile.addressSection')}>
          <ProfileRow label={t('clients.address')}>
            {addressLine ? (
              <Typography variant="body2">{addressLine}</Typography>
            ) : (
              <EmptyValue />
            )}
          </ProfileRow>
        </ProfileGroup>

        {client?.invoiceExternalId && (
          <ProfileGroup title={t('clients.profile.billingSection')}>
            <ProfileRow label={t('clients.invoiceExternalId')}>
              <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                <Typography
                  variant="body2"
                  component="span"
                  title={client.invoiceExternalId}
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {client.invoiceExternalId}
                </Typography>
                <Tooltip title={t('clients.profile.copyInvoiceId')}>
                  <IconButton
                    size="small"
                    aria-label={t('clients.profile.copyInvoiceId')}
                    onClick={() => onCopyInvoiceId?.(client.invoiceExternalId)}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </ProfileRow>
          </ProfileGroup>
        )}

        {isManager && (
          <ProfileGroup title={t('clients.pricing.section')}>
            <ProfileRow label={t('clients.pricing.certified')}>
              <Typography variant="body2">
                {formatPricingRuleSummary(resolvePricingRules(client).certified, t, DEFAULT_CURRENCY_SYMBOL)}
              </Typography>
            </ProfileRow>
            <ProfileRow label={t('clients.pricing.lots')}>
              <Typography variant="body2">
                {formatPricingRuleSummary(resolvePricingRules(client).lots, t, DEFAULT_CURRENCY_SYMBOL)}
              </Typography>
            </ProfileRow>
          </ProfileGroup>
        )}

        {isManager && (
          <ProfileGroup title={t('clients.profile.limitsSection')}>
            <ProfileRow label={t('clients.reservationLimit')}>
              <Typography variant="body2">{reservationDisplay}</Typography>
            </ProfileRow>
          </ProfileGroup>
        )}

        {showTagsSection && (
          <ProfileGroup title={t('clients.profile.tagsSection')}>
            {manual.length > 0 ? (
              <ProfileRow label={t('clients.tags.manual')}>
                <ClientTagsRow tags={manual} />
              </ProfileRow>
            ) : canManage ? (
              <ProfileRow label={t('clients.tags.manual')}>
                <Typography variant="body2" color="text.secondary">
                  {t('clients.profile.noTags')}
                </Typography>
              </ProfileRow>
            ) : null}
            {rule.length > 0 && (
              <ProfileRow label={t('clients.tags.alsoAppliedByRules')}>
                <ClientTagsRow tags={rule} />
              </ProfileRow>
            )}
          </ProfileGroup>
        )}
      </Box>
    </Paper>
  );
}
